import {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Divider,
  FormHelperText,
  GridLegacy,
  InputAdornment,
  Paper,
  Popper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  IconArrowRight,
  IconCheck,
  IconPlus,
  IconSearch,
} from '@tabler/icons-react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { bindPopper, bindTrigger } from 'material-ui-popup-state/hooks';
import usePartsV2Search, { PartSearchSchemaType } from 'hooks/usePartsV2Search';
import {
  PartTypeFilterIds,
  PartTypeFilters,
} from 'views/parts/PartsV2/Parts/constants';
import PopupState from 'material-ui-popup-state';
import { COLOR_OPTIONS } from 'types';
import { useTheme } from '@mui/material/styles';
import PartV2Cell from 'ui-component/DataGrid/Cell/PartV2Cell';
import {
  OrgPartIcons,
  OrgPartPOSTSchema,
  OrgPartSchema,
  OrgPartSchemaLite,
  OrgPartType,
  PublicPartSchemaLite,
} from 'types/clientV2/parts';
import { RenderPartV2CellProps } from 'ui-component/DataGrid/Render/RenderPartV2Cell';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import {
  useCreateOrgPartMutation,
  useGetOrgPartsQuery,
} from 'store/slices/clientV2/parts';
import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import { formatUnlimitedQuant, handleErr } from 'utils/functions';
import useSnackbar from 'hooks/useSnackbar';
import _ from 'lodash';
import { FieldErrors } from 'react-hook-form';
import { ALL, QueryFilterOperators } from 'types/api';
import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import useServerSideQuery from 'hooks/useServerSideQuery';
import WithSkeleton from 'ui-component/extended/WithSkeleton';
import { Org } from 'types/org';
import { ExtendedDialog } from 'ui-component/extended/ExtendedDialog';
import { useSelector } from 'store';
import { useGetOrgQuery } from 'store/slices/apiV1/org';
import { isCofactrUser } from 'utils/userPermissions';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import { PartSchemas, partStatusRequestTypes } from 'types/partStatus';
import CreateNewOrgPartDialog from 'ui-component/CreateNewOrgPartDialog';
import { specialCpids } from 'types/part';
import AccordionCard from 'ui-component/cards/AccordionCard';
import { actionButtonSpacing } from 'constants/themeConstants';

type PartPickerProps = {
  initiallySelectedParts?: OrgPartSchema['id'][] | null;
  onChange: (parts: OrgPartSchemaLite[] | null) => void;
  label?: string;
  multiple?: boolean;
  errors?: FieldErrors;
  orgId?: Org['id'];
  name: string;
  disabled?: boolean;
  confirmOnCreate?: boolean;
  partSourceTypeFiltersToRender?: PartTypeFilterIds[];
  initialSearchString?: string;
  partTypesToIgnore?: OrgPartType[];
  specialCpidShortcuts?: boolean;
  overrideOrgId?: Org['id'];
};

/**
 * PartV2Picker component for selecting organization parts.
 *
 * @remarks
 * This component provides a flexible part selection interface with optional multi-select,
 * initial value, and blacklisting capabilities.
 *
 * @param {Object} props - The component properties
 * @param {OrgPartSchema['id'][] | null} [props.initiallySelectedParts=null] - Pre-selected part(s) to populate the picker
 *   Only include OrgPartSchema UUIDs
 * @param {function(OrgPartSchemaLite[] | null): void} props.onChange - Callback triggered when part selection changes
 *   Receives the updated array of selected parts or null
 * @param {string} [props.label='Part'] - Custom label for the picker input
 * @param {boolean} [props.multiple=false] - Enable or disable multiple part selection
 * @param {FieldErrors} [props.errors] - Optional validation errors to display
 * @param {string} props.name - Unique identifier for the picker input
 * @param {boolean} [props.disabled=false] - Disable the entire picker input
 * @param {PartTypeFilterIds[]} props.partTypeFiltersToRender - List of Part Type Filters to use, out of Both, Org, or Public
 * @param {string} [props.initialSearchString] - Initial search string to use for the picker
 * @param {OrgPartType[]} [props.partTypesToIgnore] - List of part types to ignore when rendering results
 * @param {boolean} [props.specialCpidShortcuts=false] - Enable or disable special CPID shortcuts
 * @param {string} [props.overrideOrgId] - Override the org ID to use for the picker
 *
 * @returns {JSX.Element} Rendered part picker component
 *
 * @example
 *
 * <PartV2Picker
 *   name="altParts"
 *   onChange={handlePartChange}
 *   multiple
 * />
 */
const PartV2Picker: FC<PartPickerProps> = ({
  initiallySelectedParts = null,
  onChange,
  label = 'Part',
  multiple = false,
  errors,
  orgId,
  name,
  disabled,
  confirmOnCreate = false,
  partSourceTypeFiltersToRender: partTypeFiltersToRender,
  initialSearchString,
  partTypesToIgnore,
  specialCpidShortcuts = false,
  overrideOrgId,
}) => {
  const theme = useTheme();
  const { dispatchErrorSnackbar } = useSnackbar();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [partToConfirm, setPartToConfirm] =
    useState<PartSearchSchemaType | null>(null);
  const [createCustomPartDialogOpen, setCreateCustomPartDialogOpen] =
    useState(false);
  const [newlyCreatedPartId, setNewlyCreatedPartId] = useState<string | null>(
    null
  );
  const [newlyCreatedPartForDisplay, setNewlyCreatedPartForDisplay] =
    useState<PartSearchSchemaType | null>(null);

  const formErrorMessage = _.get(errors, name, undefined)?.message;

  const autocompleteRef = useRef<HTMLDivElement | null>(null);
  const [selectedParts, setSelectedParts] = useState<OrgPartSchemaLite[]>([]);
  const [publicPartIdToBeAdded, setPublicPartIdToBeAdded] = useState<
    string | null
  >(null);

  const { activeOrgId } = useSelector((state) => state.org);
  const { data: org } = useGetOrgQuery(activeOrgId ?? skipToken);
  const { data: user } = useGetUserQuery();
  const { viewAsStaff } = useSelector((state) => state.user);
  const isCofactr = isCofactrUser({ user, viewAsStaff });

  const selectedPartTypeFilters = useMemo(() => {
    if (org?.onlyOrgPartLibrary && !isCofactr) {
      return {
        [PartTypeFilterIds.ORG]: PartTypeFilters[PartTypeFilterIds.ORG],
      };
    }
    if (partTypeFiltersToRender) {
      return partTypeFiltersToRender.reduce((acc, id) => {
        if (PartTypeFilters[id]) {
          acc[id] = PartTypeFilters[id];
        }
        return acc;
      }, {} as Record<PartTypeFilterIds, (typeof PartTypeFilters)[PartTypeFilterIds]>);
    }
    return PartTypeFilters;
  }, [org?.onlyOrgPartLibrary, partTypeFiltersToRender, isCofactr]);

  const { data: initialParts, isLoading: isLoadingInitialParts } =
    useServerSideQuery<OrgPartSchema, OrgPartSchemaLite>(
      useGetOrgPartsQuery,
      initiallySelectedParts?.length
        ? {
            schema: [ALL],
            filters: [
              {
                field: 'id',
                operator: QueryFilterOperators.isAnyOf,
                value: initiallySelectedParts,
              },
            ],
            noCache: true,
            blended: !!overrideOrgId,
          }
        : skipToken
    );

  const { data: newlyCreatedPart } = useServerSideQuery<
    OrgPartSchema,
    OrgPartSchemaLite
  >(
    useGetOrgPartsQuery,
    newlyCreatedPartId
      ? {
          schema: [ALL],
          filters: [
            {
              field: 'id',
              operator: QueryFilterOperators.isAnyOf,
              value: [newlyCreatedPartId],
            },
          ],
          noCache: true,
          ...(overrideOrgId ? { overrideOrgId } : {}),
          blended: !!overrideOrgId,
        }
      : skipToken
  );

  useEffect(() => {
    if (initialParts?.data) {
      setSelectedParts((prevSelectedParts) => [
        ...prevSelectedParts,
        ...initialParts.data,
      ]);
    }
  }, [initialParts]);

  const lastNotifiedValue = useRef<OrgPartSchemaLite[]>([]);

  useEffect(() => {
    if (!_.isEqual(selectedParts, lastNotifiedValue.current)) {
      lastNotifiedValue.current = [...selectedParts];
      onChange(selectedParts);
    }
  }, [selectedParts, onChange]);

  const selectedPartIds = useMemo(
    () =>
      selectedParts.flatMap((part) =>
        [part.id, part.publicPart].filter(Boolean)
      ),
    [selectedParts]
  );

  const selectedOrgPartIds = useMemo(
    () => selectedParts.flatMap((part) => [part.id].filter(Boolean)),
    [selectedParts]
  );

  const [addPartToLibrary, { isLoading: isLoadingAddPartToLibrary }] =
    useCreateOrgPartMutation();

  const {
    selectedPartType,
    setSelectedPartType,
    orgParts,
    publicParts,
    bothParts,
    searchString,
    handleSetSearchString,
    handleImmediateSearch,
    supply: { supplyStatus },
  } = usePartsV2Search({
    overrideOrgId,
    defaultSelectedPartType: org?.onlyOrgPartLibrary
      ? PartTypeFilterIds.ORG
      : undefined,
  });

  const getIsSupplyLoading = useCallback(
    (cofactrId: string | null) => {
      if (!cofactrId) {
        return false;
      }

      return (
        _.get(supplyStatus[cofactrId], PartSchemas.supply) !==
        partStatusRequestTypes.SUCCESS
      );
    },
    [supplyStatus]
  );

  useEffect(() => {
    if (org?.onlyOrgPartLibrary) {
      setSelectedPartType(PartTypeFilterIds.ORG);
    } else if (partTypeFiltersToRender?.length) {
      setSelectedPartType(partTypeFiltersToRender[0]);
    }
  }, [org?.onlyOrgPartLibrary, partTypeFiltersToRender, setSelectedPartType]);

  useEffect(() => {
    if (initialSearchString) {
      handleImmediateSearch(initialSearchString);
    }
  }, [initialSearchString, handleImmediateSearch]);

  const currentParts = useMemo(() => {
    let parts;
    switch (selectedPartType) {
      case PartTypeFilterIds.ORG:
        parts = _.filter(orgParts.foundOrgParts, (orgPart) => {
          const part = orgPart.part;
          if (!part) return true;
          return (
            !('partType' in part) ||
            !partTypesToIgnore?.includes((part as OrgPartSchemaLite).partType)
          );
        });
        break;
      case PartTypeFilterIds.PUBLIC:
        parts = publicParts.foundPublicParts || [];
        break;
      default:
        parts = _.filter(bothParts.parts, (bothPart) => {
          const part = bothPart.part;
          if (!part) return true;
          return (
            !('partType' in part) ||
            !partTypesToIgnore?.includes((part as OrgPartSchemaLite).partType)
          );
        });
    }

    // Add the newly created part to the front if it exists and matches the current filter
    if (
      newlyCreatedPartForDisplay &&
      selectedPartType === PartTypeFilterIds.ORG
    ) {
      // Check if the part is already in the list to avoid duplicates
      const isAlreadyInList = parts.some(
        (existingPart) => existingPart.id === newlyCreatedPartForDisplay.id
      );

      if (!isAlreadyInList) {
        return [newlyCreatedPartForDisplay, ...parts];
      }
    }

    return parts;
  }, [
    selectedPartType,
    orgParts.foundOrgParts,
    publicParts.foundPublicParts,
    bothParts.parts,
    partTypesToIgnore,
    newlyCreatedPartForDisplay,
  ]);

  const isLoadingPartList = useMemo(() => {
    switch (selectedPartType) {
      case PartTypeFilterIds.ORG:
        return orgParts.isLoading;
      case PartTypeFilterIds.PUBLIC:
        return publicParts.isLoading;
      default:
        return bothParts.isLoading;
    }
  }, [
    selectedPartType,
    orgParts.isLoading,
    publicParts.isLoading,
    bothParts.isLoading,
  ]);

  const isErrorFetchingParts = useMemo(() => {
    if (selectedPartType === PartTypeFilterIds.PUBLIC) {
      return publicParts.isError;
    }
    if (selectedPartType === PartTypeFilterIds.BOTH) {
      return bothParts.isError;
    }
    return false;
  }, [selectedPartType, bothParts.isError, publicParts.isError]);

  const handleChangeSelectedPartType = useCallback(
    (_e: SyntheticEvent, newValue: PartTypeFilterIds) => {
      if (newValue !== null && newValue !== undefined) {
        setSelectedPartType(newValue);
      }
    },
    [setSelectedPartType]
  );

  const removePart = useCallback(
    (part: OrgPartSchemaLite, publicPartId: string | null) => {
      if (multiple) {
        setSelectedParts((prevSelectedParts) => {
          const newSelectedParts = prevSelectedParts.filter(
            (p) => p.id !== part.id && p.id !== publicPartId
          );
          return newSelectedParts;
        });
      } else {
        setSelectedParts([]);
      }
    },
    [multiple]
  );

  const addPart = useCallback(
    (part: OrgPartSchemaLite) => {
      setSelectedParts((prevSelectedParts) => {
        const newSelectedParts = multiple
          ? [...prevSelectedParts, part]
          : [part];
        return newSelectedParts;
      });
    },
    [multiple]
  );

  useEffect(() => {
    if (newlyCreatedPart?.data && newlyCreatedPart.data.length > 0) {
      const newPart = newlyCreatedPart.data[0];
      setSelectedParts((prevSelectedParts) => {
        const newSelectedParts = multiple
          ? [...prevSelectedParts, newPart]
          : [newPart];
        return newSelectedParts;
      });
      setNewlyCreatedPartId(null); // Reset after adding

      // Add the newly created part to the front of the current parts list
      // This will make it appear at the top of the search results
      const newPartWithSource: PartSearchSchemaType = {
        id: newPart.id,
        part: newPart,
        partDataType: PartTypeFilterIds.ORG,
        source: PartTypeFilterIds.ORG,
        orgPartId: newPart.id,
      };

      setNewlyCreatedPartForDisplay(newPartWithSource);
    }
  }, [newlyCreatedPart, multiple]);

  const handleSelectPart = useCallback(
    (part: OrgPartSchemaLite) => {
      const publicPart: OrgPartSchemaLite['publicPart'] | null = _.get(
        part,
        'publicPart',
        null
      );

      if (
        selectedPartIds.includes(part.id) ||
        (publicPart && selectedPartIds.includes(publicPart))
      ) {
        removePart(part, publicPart);
      } else {
        addPart(part);
      }
    },
    [selectedPartIds, removePart, addPart]
  );

  const handleAddAndSelect = useCallback(
    async (part: PartSearchSchemaType) => {
      setPublicPartIdToBeAdded(part.id);
      let newPartId;
      try {
        const newPartData = await addPartToLibrary({
          publicPartId: part.id,
          partType: OrgPartType.OTS,
          ...(orgId ? { org: orgId } : {}),
          ...(overrideOrgId ? { overrideOrgId } : {}),
        } as OrgPartPOSTSchema & { overrideOrgId?: string }).unwrap();
        newPartId = newPartData.createdIds[0];
      } catch (err) {
        handleErr(err, (_errMessage: string) => {
          dispatchErrorSnackbar('Error adding part to library');
        });
      } finally {
        setPublicPartIdToBeAdded(null);
      }

      if (newPartId) {
        const newOrgPart = {
          ...part.part,
          publicPart: part.id,
          id: newPartId,
        } as OrgPartSchemaLite;

        handleSelectPart(newOrgPart);
      }
    },
    [
      addPartToLibrary,
      orgId,
      overrideOrgId,
      dispatchErrorSnackbar,
      handleSelectPart,
    ]
  );

  const value = useMemo(
    () => (multiple ? selectedParts : selectedParts[0] ?? null),
    [multiple, selectedParts]
  );

  return (
    <>
      <PopupState variant="popper" popupId="part-picker-popup">
        {(popupState) => (
          <ClickAwayListener onClickAway={popupState.close}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Autocomplete<OrgPartSchemaLite, boolean, undefined, false>
                  ref={autocompleteRef}
                  fullWidth
                  multiple={multiple}
                  value={value}
                  options={[]}
                  getOptionLabel={(option) =>
                    option?.mpn || option?.customId || option?.cofactrId || '-'
                  }
                  disabled={disabled}
                  renderTags={(v, getTagProps) =>
                    v.map((option, index) => {
                      const partType = _.get(
                        option,
                        'partType',
                        OrgPartType.OTS
                      );
                      const Icon = OrgPartIcons[partType];
                      return (
                        <Chip
                          {...getTagProps({ index })}
                          label={
                            option?.mpn ||
                            option?.customId ||
                            option?.cofactrId ||
                            '-'
                          }
                          style={{ textTransform: 'uppercase' }}
                          color="secondary"
                          icon={<Icon />}
                          onDelete={() => {
                            handleSelectPart(option);
                          }}
                        />
                      );
                    })
                  }
                  onChange={(
                    _e: SyntheticEvent,
                    newValue: OrgPartSchemaLite | OrgPartSchemaLite[] | null
                  ) => {
                    if (!newValue) {
                      setSelectedParts([]);
                    }
                  }}
                  slotProps={{
                    popupIndicator: {
                      onClick: (e: React.MouseEvent) => {
                        e.stopPropagation();
                        popupState.isOpen
                          ? popupState.close()
                          : popupState.open();
                      },
                    },
                  }}
                  popupIcon={<ArrowDropDownIcon sx={{ cursor: 'pointer' }} />}
                  renderInput={(params) => (
                    <Box>
                      <TextField
                        {...params}
                        label={label}
                        onFocus={() => popupState.open()}
                        inputProps={{ ...params.inputProps, readOnly: true }}
                        error={!!formErrorMessage}
                        // This isn't actually a duplicate prop. Note casing of the fields.
                        /* eslint-disable-next-line react/jsx-no-duplicate-props */
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              {isLoadingInitialParts && (
                                <InputAdornment position="start">
                                  <WithSkeleton isLoading>
                                    <Typography>lorem ipsum mpn</Typography>
                                  </WithSkeleton>
                                </InputAdornment>
                              )}
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                      {formErrorMessage && (
                        <FormHelperText
                          error
                          id="standard-weight-helper-text-name"
                        >
                          {formErrorMessage}
                        </FormHelperText>
                      )}
                    </Box>
                  )}
                  PopperComponent={() => null}
                  {...bindTrigger(popupState)}
                />
              </Box>
              <Popper
                {...bindPopper(popupState)}
                anchorEl={autocompleteRef.current}
                placement="bottom"
                modifiers={[
                  {
                    name: 'sameWidth',
                    enabled: true,
                    fn: ({ state }) => {
                      const minWidth = 650;
                      const width = Math.max(
                        state.rects.reference.width,
                        minWidth
                      );
                      state.styles.popper.width = `${width}px`;
                      return state;
                    },
                    phase: 'beforeWrite',
                    requires: ['computeStyles'],
                  },
                ]}
                sx={{
                  zIndex: 1300,
                  minWidth: '650px',
                  width: autocompleteRef.current
                    ? `${autocompleteRef.current.offsetWidth}px`
                    : '650px',
                  transition: 'width 0.1s ease-out',
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    maxHeight: 500,
                    overflow: 'hidden',
                    minWidth: '650px',
                    width: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <ToggleButtonGroup
                      value={selectedPartType}
                      exclusive
                      onChange={handleChangeSelectedPartType}
                      color={COLOR_OPTIONS.success}
                      sx={{ mx: 2, my: 1 }}
                    >
                      {Object.values(selectedPartTypeFilters).map((filter) => (
                        <ToggleButton key={filter.id} value={filter.id}>
                          {filter.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                  {specialCpidShortcuts && (
                    <Box sx={{ px: 2, pb: 1 }}>
                      <AccordionCard
                        title="Generic Parts"
                        defaultExpanded={false}
                      >
                        <GridLegacy
                          item
                          container
                          xs={12}
                          spacing={actionButtonSpacing}
                        >
                          {Object.keys(specialCpids).map((cpid) => (
                            <GridLegacy item xs="auto" key={cpid}>
                              <SegmentButton
                                variant="outlined"
                                color="secondary"
                                size="small"
                                onClick={() => {
                                  handleSetSearchString(cpid);
                                  handleImmediateSearch(cpid);
                                }}
                              >
                                {specialCpids[cpid]}
                              </SegmentButton>
                            </GridLegacy>
                          ))}
                        </GridLegacy>
                      </AccordionCard>
                    </Box>
                  )}
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    <TextField
                      fullWidth
                      value={searchString}
                      onChange={(e) => handleSetSearchString(e.target.value)}
                      placeholder="Search for a part number or manufacturer"
                      size="small"
                      InputProps={{
                        onKeyDown: (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleImmediateSearch(searchString.trim());
                          }
                        },
                        startAdornment: (
                          <InputAdornment position="start">
                            <IconSearch stroke={1.5} fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Search">
                              <InputAdornment
                                position="end"
                                sx={{ cursor: 'pointer' }}
                                onClick={() =>
                                  handleImmediateSearch(searchString.trim())
                                }
                              >
                                <IconArrowRight stroke={1.5} fontSize="small" />
                              </InputAdornment>
                            </Tooltip>
                            {!overrideOrgId && (
                              <Tooltip title="Add custom part">
                                <InputAdornment
                                  position="end"
                                  sx={{ cursor: 'pointer' }}
                                  onClick={() =>
                                    setCreateCustomPartDialogOpen(true)
                                  }
                                >
                                  <IconPlus stroke={1.5} fontSize="small" />
                                </InputAdornment>
                              </Tooltip>
                            )}
                          </Box>
                        ),
                      }}
                    />
                  </Box>
                  <Divider />
                  <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
                    {isErrorFetchingParts && !isLoadingPartList && (
                      <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography variant="subtitle1" color="error">
                          Error searching parts. Please try again.
                        </Typography>
                      </Box>
                    )}
                    {isLoadingPartList ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          py: 4,
                        }}
                      >
                        <CircularProgress
                          size={40}
                          thickness={2.5}
                          sx={{ color: theme.palette.primary[600] }}
                        />
                      </Box>
                    ) : currentParts.length === 0 && !isErrorFetchingParts ? (
                      <Box
                        sx={{
                          py: 4,
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="subtitle1">
                          {!searchString.trim()
                            ? 'Enter a search term to find parts'
                            : `No parts found matching search: ${searchString.trim()}`}
                        </Typography>
                      </Box>
                    ) : (
                      currentParts.map((row) => {
                        const { part, partDataType, source } = row;
                        const publicPart = _.get(part, 'publicPart', undefined);
                        const isPublicPartDataType =
                          partDataType === PartTypeFilterIds.PUBLIC;
                        const isOnlyPublicPart =
                          source === PartTypeFilterIds.PUBLIC;
                        const isSelected =
                          partDataType === PartTypeFilterIds.ORG
                            ? selectedOrgPartIds.includes(part.id)
                            : selectedPartIds.includes(part.id) ||
                              (publicPart &&
                                selectedPartIds.includes(publicPart));
                        const props = {
                          orgPart: isPublicPartDataType
                            ? undefined
                            : (part as OrgPartSchemaLite),
                          publicPart: isPublicPartDataType
                            ? (part as PublicPartSchemaLite)
                            : undefined,
                          searchParams: searchString,
                        } as RenderPartV2CellProps;
                        const sourceType =
                          PartTypeFilters[row.source as PartTypeFilterIds];
                        return (
                          <GridLegacy
                            key={row.id}
                            container
                            justifyContent="space-between"
                            alignItems="center"
                            direction="row"
                            sx={{
                              padding: theme.spacing(1, 2),
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                              },
                              '&:last-child': {
                                borderBottom: 'none',
                              },
                            }}
                          >
                            <GridLegacy
                              container
                              item
                              xs={4}
                              justifyContent="flex-start"
                            >
                              <PartV2Cell {...props} />
                            </GridLegacy>
                            <GridLegacy
                              container
                              item
                              xs={3.5}
                              justifyContent="center"
                            >
                              <Stack
                                direction="row"
                                spacing={3}
                                justifyContent="center"
                                alignItems="center"
                              >
                                {['buyable', 'quotable', 'maybe'].map(
                                  (stockType) => {
                                    const supplyData = _.get(
                                      row?.supply,
                                      stockType,
                                      undefined
                                    ) as number | undefined;
                                    const isSupplyLoading = getIsSupplyLoading(
                                      row?.part?.cofactrId
                                    );
                                    return (
                                      <Stack
                                        key={stockType}
                                        direction="column"
                                        justifyContent="center"
                                        alignItems="center"
                                      >
                                        <WithSkeleton
                                          isLoading={isSupplyLoading}
                                        >
                                          <Typography>
                                            {formatUnlimitedQuant(
                                              supplyData ?? 0
                                            )}
                                          </Typography>
                                        </WithSkeleton>
                                        <Typography variant="subtitle2">
                                          {stockType}
                                        </Typography>
                                      </Stack>
                                    );
                                  }
                                )}
                              </Stack>
                            </GridLegacy>
                            <GridLegacy
                              container
                              item
                              xs={1.5}
                              justifyContent="flex-end"
                            >
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                justifyContent="flex-start"
                              >
                                {_.map(sourceType?.icons, (icon) => {
                                  const Icon = icon?.Icon;
                                  return (
                                    <Tooltip key={icon.id} title={icon.label}>
                                      {/* Span added to properly handle rendering tooltip for custom svg Icon */}
                                      <span>
                                        <Icon />
                                      </span>
                                    </Tooltip>
                                  );
                                })}
                              </Stack>
                            </GridLegacy>
                            <GridLegacy
                              container
                              item
                              xs={3}
                              justifyContent="flex-end"
                            >
                              {isOnlyPublicPart && !isSelected && (
                                <SegmentLoadingButton
                                  variant="outlined"
                                  startIcon={<IconPlus size={16} />}
                                  onClick={async () => {
                                    if (confirmOnCreate) {
                                      setPartToConfirm(row);
                                      setConfirmDialogOpen(true);
                                    } else {
                                      await handleAddAndSelect(row);
                                      !multiple && popupState.close();
                                    }
                                  }}
                                  loading={
                                    isLoadingAddPartToLibrary &&
                                    publicPartIdToBeAdded === row.part.id
                                  }
                                >
                                  Add & Select
                                </SegmentLoadingButton>
                              )}
                              {!isOnlyPublicPart && !isSelected && (
                                <SegmentButton
                                  variant="outlined"
                                  startIcon={<IconCheck size={16} />}
                                  color={isSelected ? 'primary' : 'inherit'}
                                  onClick={() => {
                                    const partToSelect = isPublicPartDataType
                                      ? ({
                                          ...row.part,
                                          publicPart: row.id,
                                          id: row.orgPartId,
                                        } as OrgPartSchemaLite)
                                      : (row.part as OrgPartSchemaLite);
                                    handleSelectPart(partToSelect);
                                    !multiple && popupState.close();
                                  }}
                                >
                                  Select
                                </SegmentButton>
                              )}
                              {isSelected && (
                                <Typography
                                  color={theme.palette.success.main}
                                  sx={{ pr: 2 }}
                                >
                                  Selected
                                </Typography>
                              )}
                            </GridLegacy>
                          </GridLegacy>
                        );
                      })
                    )}
                  </Box>
                </Paper>
              </Popper>
            </Box>
          </ClickAwayListener>
        )}
      </PopupState>
      {confirmOnCreate && (
        <ExtendedDialog
          open={confirmDialogOpen}
          onCloseDialog={() => {
            setConfirmDialogOpen(false);
            setPartToConfirm(null);
          }}
          title="Confirm Add Part"
          submitButtonCopy="Add & Select"
          onClickPrimaryButton={async () => {
            if (partToConfirm) {
              await handleAddAndSelect(partToConfirm);
              setConfirmDialogOpen(false);
              setPartToConfirm(null);
            }
          }}
        >
          <Typography>
            Are you sure you want to add {partToConfirm?.part.mpn} to your
            library?
          </Typography>
        </ExtendedDialog>
      )}
      {createCustomPartDialogOpen && !overrideOrgId && (
        <CreateNewOrgPartDialog
          open={createCustomPartDialogOpen}
          onClose={() => setCreateCustomPartDialogOpen(false)}
          rawPart={{
            mpn: searchString,
          }}
          onCreateSuccess={(newPartId) => {
            setCreateCustomPartDialogOpen(false);
            if (newPartId) {
              // Set the newly created part ID to trigger fetching its data
              setNewlyCreatedPartId(newPartId);
            }
          }}
        />
      )}
    </>
  );
};

export default PartV2Picker;
